import { Button, Card, FooterAmount } from 'components/atoms';
import TotalCard from 'components/molecules/card/total/TotalCard';
import ListWrapper from 'components/molecules/list-wrapper/ListWrapper';

import { QueryFilter, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useScroll } from 'components/templates/application/ApplicationTemplate';
import { useComponentVisible } from 'hooks';

import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';

import {
  useGetLabelReportDetailedFile,
  useGetLabelReportFile,
  useLabelReportsListInfinite,
} from 'services/label-report';
import { ILabelReportsByLabelResponse } from 'services/label-report/types';
import { useGetSettlementPeriodsLightList } from 'services/settlement';
import { IStatementsSearch } from 'services/settlement/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

import { FormInput, MagellanPopups } from 'components/molecules';
import { formatCurrency } from 'utils/global/currency.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { getNodes } from 'utils/table/nodes.utils';
import { PiDownload } from 'react-icons/pi';
import { useStreamFileDownload } from 'hooks/useStreamFileDownload';
import QueryFilterLabels from 'components/organisms/filters/query-form/labels/QueryFilterLabels';
import { useEffect } from 'react';
import { registerLabelReportsQueryFilters } from 'queryFilters/labelReportsQueryFilter';

enum SORT_COLUMNS {
  startSettlementPeriod,
  endSettlementPeriod,
  net,
  settlementNumber,
}

const statementsSearchInitialValues: IStatementsSearch = {
  startSettlementPeriodId: '',
  endSettlementPeriodId: '',
};

const LabelRaport = () => {
  const columnsVisibility = useTableColumnsVisibility('LabelReport1', SORT_COLUMNS);

  const { searchById, setSearchById } = useStoreState();
  const filters = searchById[columnsVisibility.tableId] || statementsSearchInitialValues;

  const { data, isLoading, fetchNextPage, isFetchingNextPage, statistics } =
    useLabelReportsListInfinite(filters);

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const { setFile } = useStreamFileDownload(useGetLabelReportFile);

  const scroll = useScroll({ fetchNextPage: fetchNextPage });
  const settlementPeriods = useGetSettlementPeriodsLightList();

  const settlementPeriodOptions = settlementPeriods.data?.flat().map((s) => ({
    label: getSettlementPeriodName(s.settlementPeriodName),
    value: s.id,
  }));
  const totalValueLength = formatCurrency(statistics?.allAmountEarned ?? 0).length;

  const tableData = getNodes<ILabelReportsByLabelResponse>(data?.pages.flat());

  const { t } = useTranslation();

  const i18nextPrefix = 'label-reports';

  useEffect(() => {
    if (!settlementPeriodOptions?.length) return;

    const validOptions = settlementPeriodOptions.flatMap((o) =>
      o.label ? [{ value: o.value, label: o.label }] : [],
    );

    registerLabelReportsQueryFilters(validOptions);
  }, [settlementPeriodOptions]);

  return (
    <TopbarPage
      topbarAbsolute
      footer={
        <TotalCard
          header={t('table.total')}
          content={<FooterAmount statistic={statistics?.allAmountForLabel} />}
        />
      }
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          customBackItem={<div />}
          topbarContent={
            <>
              <div className="flex">
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
              </div>
            </>
          }
        >
          <QueryFilter<IStatementsSearch> i18nextPrefix={i18nextPrefix} initialValues={filters}>
            <>
              <QueryFilter.Form<IStatementsSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={(values) => setSearchById(columnsVisibility.tableId, values)}
              >
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name="startSettlementPeriodId"
                    label={t('input.label.startSettlementPeriod')}
                    placeholder=""
                    options={settlementPeriodOptions}
                    appBackground
                  />
                  <FormInput.Select
                    name="endSettlementPeriodId"
                    placeholder=""
                    label={t('input.label.endSettlementPeriod')}
                    options={settlementPeriodOptions}
                    appBackground
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilterLabels scope="labelReport" isFormOpen={isComponentVisible} />
            </>
          </QueryFilter>
        </TopBar>
      }
    >
      <ListWrapper
        view="list"
        style={{ paddingBottom: 100 }}
        items={data?.pages.flat()}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        item={(labelReport) => (
          <Card
            view="list"
            key={labelReport.id}
            header={
              labelReport.startSettlementPeriodId === labelReport.endSettlementPeriodId
                ? getSettlementPeriodName(labelReport.startSettlementPeriodName)
                : `${getSettlementPeriodName(
                    labelReport.startSettlementPeriodName,
                  )} - ${getSettlementPeriodName(labelReport.endSettlementPeriodName)}`
            }
            description={labelReport.labelReportNumber}
            status={
              <div className="flex w-max font-bold items-center gap-4" style={{ fontSize: 18 }}>
                {formatCurrency(labelReport.amountForLabel)}

                <MagellanPopups.FilePreviewPopup
                  fileId={labelReport.id}
                  fileName={labelReport.labelReportNumber ?? ''}
                  fileService={useGetLabelReportFile}
                  trigger={
                    <Button type="button" isClear onlyIcon disabled={!labelReport.filePath}>
                      <Button.Icon icon={<PiDownload />} />
                    </Button>
                  }
                  onDownload={() =>
                    setFile({
                      id: labelReport.id,
                      name: labelReport.labelReportNumber,
                    })
                  }
                />
              </div>
            }
          />
        )}
      />
      {/* <FooterTable
        id={columnsVisibility.tableId}
        // select={select}
        columns={[
          {
            label: t("input.label.startSettlementPeriod"),
            renderCell: (item) => (
              <TableItem>
                {item.startSettlementPeriodName?.name && (
                  <TableItem.Text
                    text={getSettlementPeriodName(
                      item.startSettlementPeriodName
                    )}
                  />
                )}
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.startSettlementPeriod),
            footer: t("table.total"),
          },

          {
            label: t("input.label.endSettlementPeriod"),
            renderCell: (item) => (
              <TableItem>
                {item.endSettlementPeriodName?.name && (
                  <TableItem.Text
                    text={getSettlementPeriodName(item.endSettlementPeriodName)}
                  />
                )}
              </TableItem>
            ),
            hide: columnsVisibility.hide(SORT_COLUMNS.endSettlementPeriod),
            resize: true,
          },

          {
            label: t("input.label.settlementNumber"),
            renderCell: ({ labelReportNumber }) => (
              <TableItem>
                <TableItem.Text text={labelReportNumber} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.settlementNumber),
          },
          {
            label: t("input.label.amountEarned"),
            renderCell: ({ amountForLabel }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(amountForLabel, {
                    length: totalValueLength,
                  })}
                />
              </TableItem>
            ),
            footer: formatCurrency(
              labelReportStatistic.data?.allAmountForLabel,
              {
                length: totalValueLength,
              }
            ),
            hide: columnsVisibility.hide(SORT_COLUMNS.net),
          },

          {
            label: "",
            renderCell: (item) => (
              <TableItem justifyPosition="end">
                {
                  <div className="flex">
                    <Button
                      type="button"
                      isClear
                      onlyIcon
                      disabled={!item.filePath}
                    >
                      <Button.Icon icon={<PiDownload />} />
                    </Button>
                  </div>
                }
              </TableItem>
            ),
            resize: true,
          },
        ]}
        displayMode
        loading={isLoading || isFetchingNextPage}
        data={tableData}
        onFullyScroll={fetchNextPage}
      /> */}
    </TopbarPage>
  );
};

export default LabelRaport;
